/**
 * MD-24 FE-HOTNEWS - i18n contract
 *
 * Threads-style social feed for legal news + side-by-side USP detail.
 * Source: marketer deliverable 2026-05-05 (Hot News S5 prep).
 *
 * Reference:
 *   - F-FE-HOTNEWS.md (frontend spec)
 *   - docs/superpowers/specs/2026-05-05-hot-news-design.md (design source)
 *   - DECISIONS.md#2026-05-05-hot-news-promote-day1
 */

import type { Lang, MaybeVN } from './landing-types';

export type HotNewsCopy = {
  // Feed page meta + header
  feed_meta_title: string;
  feed_meta_description: string;
  feed_header_title: string;
  feed_header_subtitle: string;
  feed_intro_card: string;

  // Filter bar
  filter_bar_prompt: string;
  filter_topic_label: string;
  filter_source_label: string;
  filter_tag_label: string;
  filter_clear: string;
  filter_active_count: string; // {n}

  // News thread card micro-actions
  action_like: string;
  action_like_active: string;
  action_save: string;
  action_save_active: string;
  action_share: string;
  action_share_copied: string;
  action_ask: string;
  card_source_prefix: string;
  card_time_relative_now: string;
  card_time_minutes: string; // {n}
  card_time_hours: string; // {n}
  card_time_days: string; // {n}
  card_summary_label: string;
  card_citation_label: string;
  card_read_full: string;

  // Digest signup block
  digest_block_eyebrow: string;
  digest_block_title: string;
  digest_block_body: string;
  digest_email_label: string;
  digest_email_placeholder: string;
  digest_topics_label: string;
  digest_topic_general: string;
  digest_topic_lao_dong: string;
  digest_topic_thue: string;
  digest_topic_visa: string;
  digest_topic_dat_dai: string;
  digest_topic_hon_nhan: string;
  digest_topic_hinh_su: string;
  digest_topic_kinh_doanh: string;
  digest_topic_hanh_chinh: string;
  digest_topic_chinh_tri_nhay_cam: string;
  digest_topic_an_ninh_quoc_phong: string;
  digest_topic_nhan_su_lanh_dao: string;
  digest_topic_ton_giao_dan_toc: string;
  digest_topic_tu_phap_xet_xu: string;
  digest_topic_khac: string;
  digest_consent_checkbox: string;
  digest_consent_pdpa_link: string;
  digest_submit: string;
  digest_submit_loading: string;
  digest_success_title: string;
  digest_success_body: string; // {email}
  digest_error_invalid_email: string;
  digest_error_consent_required: string;
  digest_error_topic_required: string;
  digest_error_rate_limit: string;

  // Email digest
  email_subject_mon: string; // {DD/MM}
  email_subject_tue: string; // {DD/MM}
  email_subject_wed: string; // {DD/MM}
  email_subject_thu: string; // {DD/MM}
  email_subject_fri: string; // {DD/MM}
  email_intro: string; // {first_name_or_blank}
  email_signoff: string;
  email_footer_disclaimer: string;
  email_footer_unsub_prefix: string;
  email_footer_manage_link: string;
  email_footer_unsubscribe_link: string;

  // Unsubscribe page
  unsub_title: string;
  unsub_body: string;
  unsub_resubscribe: string;
  unsub_feedback_prompt: string;
  unsub_reason_too_many: string;
  unsub_reason_not_relevant: string;
  unsub_reason_quality: string;
  unsub_reason_other: string;

  // Ask CTA
  ask_cta_card: string;
  ask_cta_detail: string;
  ask_cta_aria: string;
  ask_prefill_hint: string; // {headline}

  // Empty states
  empty_feed_cold: string;
  empty_topic_filter: string; // {topic} {reset_url} {digest_url}
  empty_tag_filter: string; // {tag}
  empty_source_filter: string; // {source}
  empty_saved_anon: string;

  // Error states
  error_connector_partial: string;
  error_cache_timeout: string;
  error_summary_failed: string;
  error_detail_404: string;
  error_ask_unavailable: string; // {back_url} {retry_url}
  error_subscribe_rate_limit: string;
  error_network_offline: string;

  // Attribution footer
  attribution_source_label: string; // {source_name} {source_url}
  attribution_takedown_label: string;
  attribution_takedown_email: string;
  attribution_takedown_form_label: string;
  attribution_disclaimer_label: string;

  // Playful tag display labels (10)
  tag_vua_ban_hanh: string;
  tag_sap_hieu_luc: string;
  tag_dang_hot: string;
  tag_drama_phap_ly: string;
  tag_vu_an_theo_doi: string;
  tag_luat_doi_moi: string;
  tag_quy_dinh_la_lung: string;
  tag_trending_tuan: string;
  tag_phap_luat_kinh_doanh: string;
  tag_cong_dan_can_biet: string;
};

export type HotNewsCatalog<L extends Lang> = L extends 'vi'
  ? HotNewsCopy
  : { [K in keyof HotNewsCopy]: MaybeVN };

export const HOTNEWS_VI: HotNewsCopy = {
  feed_meta_title: 'Tin pháp luật mỗi ngày - Asklaw',
  feed_meta_description:
    'Tin pháp luật Việt Nam mỗi ngày, kèm trích dẫn Điều/Khoản và giải thích từ Asklaw AI.',
  feed_header_title: 'Tin pháp luật',
  feed_header_subtitle:
    'Mỗi ngày một dòng tin, Asklaw đọc các nguồn chính thống mỗi sáng, tóm tắt và liên kết với văn bản pháp luật gốc. Bạn lướt - chúng tôi tra.',
  feed_intro_card: '',

  filter_bar_prompt: 'Lọc theo',
  filter_topic_label: 'Chủ đề',
  filter_source_label: 'Nguồn',
  filter_tag_label: 'Loại tin',
  filter_clear: 'Xoá bộ lọc',
  filter_active_count: '{n} bộ lọc đang áp dụng',

  action_like: 'Quan tâm',
  action_like_active: 'Đã quan tâm',
  action_save: 'Lưu',
  action_save_active: 'Đã lưu',
  action_share: 'Chia sẻ',
  action_share_copied: 'Đã sao chép link',
  action_ask: 'Hỏi Asklaw',
  card_source_prefix: 'Nguồn:',
  card_time_relative_now: 'vừa xong',
  card_time_minutes: '{n} phút trước',
  card_time_hours: '{n} giờ trước',
  card_time_days: '{n} ngày trước',
  card_summary_label: 'Asklaw tóm tắt',
  card_citation_label: 'Trích dẫn',
  card_read_full: 'Xem chi tiết',

  digest_block_eyebrow: 'Bản tin sáng',
  digest_block_title: '5 tin pháp luật mỗi sáng, gửi vào email bạn',
  digest_block_body:
    'Asklaw lọc 6 nguồn chính thống, tóm tắt 60-100 từ kèm trích dẫn Điều/Khoản. Đọc 2 phút, hiểu cả tuần.',
  digest_email_label: 'Email của bạn',
  digest_email_placeholder: 'ban@email.com',
  digest_topics_label: 'Chủ đề bạn quan tâm (chọn ít nhất 1)',
  digest_topic_general: 'Tổng hợp',
  digest_topic_lao_dong: 'Lao động & BHXH',
  digest_topic_thue: 'Thuế',
  digest_topic_visa: 'Visa & lưu trú',
  digest_topic_dat_dai: 'Đất đai',
  digest_topic_hon_nhan: 'Hôn nhân & gia đình',
  digest_topic_hinh_su: 'Hình sự',
  digest_topic_kinh_doanh: 'Kinh doanh & doanh nghiệp',
  digest_topic_hanh_chinh: 'Hành chính',
  digest_topic_chinh_tri_nhay_cam: 'Chính trị',
  digest_topic_an_ninh_quoc_phong: 'An ninh & quốc phòng',
  digest_topic_nhan_su_lanh_dao: 'Nhân sự lãnh đạo',
  digest_topic_ton_giao_dan_toc: 'Tôn giáo & dân tộc',
  digest_topic_tu_phap_xet_xu: 'Tư pháp & xét xử',
  digest_topic_khac: 'Chủ đề khác',
  digest_consent_checkbox:
    'Tôi đồng ý cho Asklaw lưu email và gửi bản tin pháp luật theo các chủ đề tôi đã chọn. Tôi có thể huỷ đăng ký bất kỳ lúc nào qua link trong email.',
  digest_consent_pdpa_link:
    'Xem Chính sách bảo mật và quyền của bạn theo Luật Bảo vệ dữ liệu cá nhân 2026.',
  digest_submit: 'Đăng ký nhận bản tin',
  digest_submit_loading: 'Đang xử lý...',
  digest_success_title: 'Đã đăng ký. Kiểm tra email để xác nhận.',
  digest_success_body:
    'Asklaw vừa gửi link xác nhận đến {email}. Mở email và bấm xác nhận để bắt đầu nhận bản tin sáng mai.',
  digest_error_invalid_email: 'Email chưa hợp lệ. Vui lòng kiểm tra lại.',
  digest_error_consent_required: 'Vui lòng đồng ý điều khoản trước khi đăng ký.',
  digest_error_topic_required: 'Chọn ít nhất 1 chủ đề bạn quan tâm.',
  digest_error_rate_limit: 'Bạn đã đăng ký gần đây. Vui lòng thử lại sau ít phút.',

  email_subject_mon: 'Bản tin pháp luật {DD/MM} - 5 tin Asklaw đọc giúp bạn',
  email_subject_tue: '{DD/MM} - Tin pháp luật sáng nay, có Điều/Khoản',
  email_subject_wed: 'Asklaw sáng {DD/MM}: thuế, lao động, đất đai',
  email_subject_thu: 'Cập nhật pháp luật {DD/MM} - đọc 2 phút',
  email_subject_fri: 'Cuối tuần này có gì mới? Bản tin Asklaw {DD/MM}',
  email_intro:
    'Chào {first_name_or_blank}, đây là 5 tin pháp luật Asklaw đọc giúp bạn sáng nay. Mỗi tin có tóm tắt 60-100 từ và link trích dẫn Điều/Khoản từ văn bản gốc. Bấm vào "Hỏi Asklaw" nếu bạn muốn tra sâu hơn.',
  email_signoff: '- Đội biên tập Asklaw',
  email_footer_disclaimer:
    'Asklaw chỉ tóm tắt và giới thiệu thông tin pháp luật từ các nguồn chính thống. Đây không phải tư vấn pháp lý cho trường hợp cụ thể. Vui lòng tham khảo luật sư có chứng chỉ hành nghề khi cần.',
  email_footer_unsub_prefix:
    'Bạn nhận được email này vì đã đăng ký nhận bản tin tại asklaw.vn.',
  email_footer_manage_link: 'Quản lý chủ đề',
  email_footer_unsubscribe_link: 'Huỷ đăng ký một lần bấm',

  unsub_title: 'Đã huỷ đăng ký',
  unsub_body:
    'Bạn sẽ không nhận thêm email bản tin từ Asklaw. Email của bạn được gỡ khỏi danh sách trong vòng 24 giờ.',
  unsub_resubscribe: 'Đăng ký lại',
  unsub_feedback_prompt: 'Bạn có thể chia sẻ lý do (không bắt buộc):',
  unsub_reason_too_many: 'Quá nhiều email',
  unsub_reason_not_relevant: 'Không đúng chủ đề tôi quan tâm',
  unsub_reason_quality: 'Chất lượng tin chưa tốt',
  unsub_reason_other: 'Lý do khác',

  ask_cta_card: 'Hỏi Asklaw về tin này',
  ask_cta_detail: 'Hỏi sâu hơn về tin này với Asklaw',
  ask_cta_aria: 'Mở Asklaw chat để tra sâu hơn về tin này',
  ask_prefill_hint: 'Bạn đang tra cứu tin: "{headline}". Đặt câu hỏi của bạn.',

  empty_feed_cold:
    'Asklaw đang đọc tin sáng nay. Bản tin sẽ xuất hiện sau ít phút. Trong lúc đó, bạn có thể tra cứu trực tiếp văn bản pháp luật.',
  empty_topic_filter:
    'Chưa có tin nào về chủ đề "{topic}" trong 7 ngày qua. Thử chủ đề khác hoặc đăng ký bản tin để không bỏ lỡ khi có.',
  empty_tag_filter: 'Chưa có tin gắn nhãn "{tag}" lúc này.',
  empty_source_filter: 'Chưa có tin từ {source} trong 7 ngày qua.',
  empty_saved_anon: 'Bạn chưa lưu tin nào. Bấm "Lưu" trên bất kỳ tin nào để đọc lại sau.',

  error_connector_partial:
    'Một vài nguồn tin tạm thời không truy cập được. Asklaw vẫn đang đọc các nguồn còn lại - bản tin có thể ngắn hơn thường lệ hôm nay.',
  error_cache_timeout: 'Hệ thống đang tải. Vui lòng thử lại sau 1 phút.',
  error_summary_failed:
    'Asklaw đang phân tích tin này. Trong lúc đó, bạn có thể đọc trích đoạn gốc bên dưới.',
  error_detail_404:
    'Tin này không còn khả dụng. Có thể đã được gỡ theo yêu cầu của nguồn. Quay lại bản tin.',
  error_ask_unavailable:
    'Asklaw chat tạm thời không sẵn sàng. Bạn có thể đọc lại tin hoặc thử lại.',
  error_subscribe_rate_limit:
    'Bạn đã đăng ký gần đây. Vui lòng thử lại sau ít phút.',
  error_network_offline: 'Mất kết nối mạng. Tin đã tải sẽ vẫn hiển thị.',

  attribution_source_label:
    'Tin tóm tắt từ {source_name}. Bản gốc thuộc bản quyền {source_name}. Asklaw chỉ trích dẫn theo Điều 25 Luật Sở hữu trí tuệ và Điều 38 Luật Báo chí.',
  attribution_takedown_label:
    'Nếu bạn là chủ sở hữu nội dung và muốn gỡ tin này, vui lòng gửi email hoặc dùng biểu mẫu. Asklaw cam kết phản hồi trong 48 giờ.',
  attribution_takedown_email: 'legal@asklaw.vn',
  attribution_takedown_form_label: 'Yêu cầu gỡ tin',
  attribution_disclaimer_label:
    'Tóm tắt do Asklaw AI tạo dựa trên thông tin từ nguồn. Đây không phải tư vấn pháp lý. Vui lòng tham khảo luật sư có chứng chỉ hành nghề cho trường hợp cụ thể của bạn.',

  tag_vua_ban_hanh: 'Vừa ban hành',
  tag_sap_hieu_luc: 'Sắp hiệu lực',
  tag_dang_hot: 'Đang được quan tâm',
  tag_drama_phap_ly: 'Vụ việc đáng chú ý',
  tag_vu_an_theo_doi: 'Đang theo dõi',
  tag_luat_doi_moi: 'Luật thay đổi',
  tag_quy_dinh_la_lung: 'Đáng lưu ý',
  tag_trending_tuan: 'Nổi bật tuần này',
  tag_phap_luat_kinh_doanh: 'Cho doanh nghiệp',
  tag_cong_dan_can_biet: 'Người dân nên biết',
};

export const HOTNEWS_EN: HotNewsCatalog<'en'> = {
  feed_meta_title: 'Daily legal news - Asklaw',
  feed_meta_description:
    'Vietnam legal news daily, with article-clause citations and AI explainers from Asklaw.',
  feed_header_title: 'Legal news',
  feed_header_subtitle:
    'One brief a day. Asklaw reads trusted sources every morning, summarises, and links to the original legal text. You scroll - we look it up.',
  feed_intro_card: '',

  filter_bar_prompt: 'Filter by',
  filter_topic_label: 'Topic',
  filter_source_label: 'Source',
  filter_tag_label: 'Type',
  filter_clear: 'Clear filters',
  filter_active_count: '{n} filters applied',

  action_like: 'Interested',
  action_like_active: 'Saved interest',
  action_save: 'Save',
  action_save_active: 'Saved',
  action_share: 'Share',
  action_share_copied: 'Link copied',
  action_ask: 'Ask Asklaw',
  card_source_prefix: 'Source:',
  card_time_relative_now: 'just now',
  card_time_minutes: '{n} min ago',
  card_time_hours: '{n}h ago',
  card_time_days: '{n}d ago',
  card_summary_label: 'Asklaw summary',
  card_citation_label: 'Citation',
  card_read_full: 'View detail',

  digest_block_eyebrow: 'Morning brief',
  digest_block_title: '5 Vietnam legal updates each morning, in your inbox',
  digest_block_body:
    "Asklaw filters 6 trusted sources, summarises in 60-100 words with article-clause citations. 2-minute read, week's worth of context.",
  digest_email_label: 'Your email',
  digest_email_placeholder: 'you@email.com',
  digest_topics_label: 'Topics you care about (pick at least 1)',
  digest_topic_general: 'General brief',
  digest_topic_lao_dong: 'Labour & social insurance',
  digest_topic_thue: 'Tax',
  digest_topic_visa: 'Visa & residency',
  digest_topic_dat_dai: 'Land',
  digest_topic_hon_nhan: 'Family',
  digest_topic_hinh_su: 'Criminal',
  digest_topic_kinh_doanh: 'Business',
  digest_topic_hanh_chinh: 'Administrative',
  digest_topic_chinh_tri_nhay_cam: 'Politics',
  digest_topic_an_ninh_quoc_phong: 'Security & defense',
  digest_topic_nhan_su_lanh_dao: 'Leadership appointments',
  digest_topic_ton_giao_dan_toc: 'Religion & ethnicity',
  digest_topic_tu_phap_xet_xu: 'Justice & trials',
  digest_topic_khac: 'Other topics',
  digest_consent_checkbox:
    'I consent to Asklaw storing my email and sending the legal brief on the topics I selected. I can unsubscribe anytime via the link in each email.',
  digest_consent_pdpa_link:
    'See our Privacy Policy and your rights under the Personal Data Protection Law 2026.',
  digest_submit: 'Subscribe to brief',
  digest_submit_loading: 'Processing...',
  digest_success_title: 'Subscribed. Check your inbox to confirm.',
  digest_success_body:
    "Asklaw just sent a confirmation link to {email}. Click it to start receiving tomorrow's brief.",
  digest_error_invalid_email: 'Email looks off. Please check again.',
  digest_error_consent_required: 'Please agree to the terms before subscribing.',
  digest_error_topic_required: 'Pick at least 1 topic.',
  digest_error_rate_limit: 'You subscribed recently. Try again in a few minutes.',

  email_subject_mon: 'Asklaw brief {DD/MM} - 5 legal updates worth knowing',
  email_subject_tue: "{DD/MM} - Today's legal updates, with citations",
  email_subject_wed: 'Asklaw {DD/MM}: tax, labour, land',
  email_subject_thu: 'Legal update {DD/MM} - 2-min read',
  email_subject_fri: "What's new this week? Asklaw brief {DD/MM}",
  email_intro:
    'Hi {first_name_or_blank}, here are 5 legal updates Asklaw read for you today. Each carries a 60-100-word summary and a link to the article-clause from the source. Click "Ask Asklaw" if you want to go deeper.',
  email_signoff: '- Asklaw Editorial Team',
  email_footer_disclaimer:
    'Asklaw summarises and links legal information from official sources. This is not legal advice for any specific situation. Please consult a licensed lawyer for case-specific advice.',
  email_footer_unsub_prefix: "You're receiving this because you subscribed at asklaw.vn.",
  email_footer_manage_link: 'Manage topics',
  email_footer_unsubscribe_link: 'Unsubscribe one-click',

  unsub_title: 'Unsubscribed',
  unsub_body:
    "You'll no longer receive Asklaw briefs. Your email is removed from the list within 24 hours.",
  unsub_resubscribe: 'Resubscribe',
  unsub_feedback_prompt: 'Optional - tell us why you left:',
  unsub_reason_too_many: 'Too frequent',
  unsub_reason_not_relevant: 'Not relevant to me',
  unsub_reason_quality: 'Quality issues',
  unsub_reason_other: 'Other',

  ask_cta_card: 'Ask Asklaw about this',
  ask_cta_detail: 'Ask deeper with Asklaw',
  ask_cta_aria: 'Open Asklaw chat to explore this further',
  ask_prefill_hint: 'Context loaded: "{headline}". Ask your question.',

  empty_feed_cold:
    "Asklaw is reading today's news. Stories will appear shortly. In the meantime, you can search the legal corpus.",
  empty_topic_filter:
    'No stories on "{topic}" in the past 7 days. Try another topic or subscribe to the brief to catch the next one.',
  empty_tag_filter: 'No stories tagged "{tag}" right now.',
  empty_source_filter: 'No stories from {source} in the past 7 days.',
  empty_saved_anon: 'No saved stories yet. Tap "Save" on any card to read later.',

  error_connector_partial:
    "Some sources are temporarily unreachable. Asklaw is still reading the rest - today's feed may be shorter than usual.",
  error_cache_timeout: 'System is loading. Please retry in 1 minute.',
  error_summary_failed:
    'Asklaw is analysing this story. In the meantime, see the source excerpt below.',
  error_detail_404:
    'This story is no longer available. It may have been removed per source request. Back to feed.',
  error_ask_unavailable:
    'Asklaw chat is temporarily unavailable. You can re-read the story or try again.',
  error_subscribe_rate_limit: 'You subscribed recently. Try again in a few minutes.',
  error_network_offline: 'Network offline. Cached stories remain visible.',

  attribution_source_label:
    'Summary derived from {source_name}. Original copyright belongs to {source_name}. Asklaw cites under Article 25 IP Law and Article 38 Press Law.',
  attribution_takedown_label:
    'If you own the content and want it removed, email or use the takedown form. Asklaw responds within 48 hours.',
  attribution_takedown_email: 'legal@asklaw.vn',
  attribution_takedown_form_label: 'Takedown',
  attribution_disclaimer_label:
    'Summary AI-generated from source. This is not legal advice. Please consult a licensed lawyer for your specific situation.',

  tag_vua_ban_hanh: 'Just published',
  tag_sap_hieu_luc: 'Effective soon',
  tag_dang_hot: 'Trending now',
  tag_drama_phap_ly: 'Notable case',
  tag_vu_an_theo_doi: 'Following up',
  tag_luat_doi_moi: 'Law changed',
  tag_quy_dinh_la_lung: 'Worth noting',
  tag_trending_tuan: "This week's top",
  tag_phap_luat_kinh_doanh: 'For businesses',
  tag_cong_dan_can_biet: 'Citizens should know',
};

/**
 * Tag color tokens with derived `textLight` for WCAG AA compliance.
 *
 * Field meaning:
 *   - `light`: brand hue used for chip border + 10% tinted background in light
 *     mode, AND for dot/decoration in dark mode (where filter brightness
 *     1.15 + saturate 1.1 is applied).
 *   - `dark`:  brand hue used for chip text + 14% tinted background in dark
 *     mode (passes >=4.5:1 on #0E0E0F page bg per S7 Group A audit).
 *   - `textLight`: pre-darkened (light − 30% black) used as chip *text* in
 *     light mode. Pairs with `light @ 10%` tint bg over `--asklaw-cream`
 *     #FAF9F7 to give >= 4.5:1 contrast for normal-weight text per WCAG AA.
 *
 * Audit log (S7 Group A 2026-05-05): all 10 chips now ≥5.04:1 light mode,
 * ≥4.76:1 dark mode. See `reports/coordination/SESSION-2026-05-05-SUMMARY.md`
 * VETO entry hotnews-s7-group-a.
 */
export const HOTNEWS_TAG_COLOR_TOKENS = {
  vua_ban_hanh: { light: '#16A34A', dark: '#22C55E', textLight: '#0F7234' },
  sap_hieu_luc: { light: '#EA580C', dark: '#FB923C', textLight: '#A43E08' },
  dang_hot: { light: '#DB2777', dark: '#EC4899', textLight: '#991B53' },
  drama_phap_ly: { light: '#7C3AED', dark: '#A78BFA', textLight: '#5729A6' },
  vu_an_theo_doi: { light: '#2563EB', dark: '#60A5FA', textLight: '#1A45A5' },
  luat_doi_moi: { light: '#4F46E5', dark: '#818CF8', textLight: '#3731A0' },
  quy_dinh_la_lung: { light: '#D97706', dark: '#FBBF24', textLight: '#985304' },
  trending_tuan: { light: '#E11D48', dark: '#FB7185', textLight: '#9E1432' },
  phap_luat_kinh_doanh: { light: '#0891B2', dark: '#22D3EE', textLight: '#06667D' },
  cong_dan_can_biet: { light: '#0D9488', dark: '#2DD4BF', textLight: '#09685F' },
} as const;
